import { useCallback, useRef } from "react";
import { ROOT_PARENT_ID } from "./treeOps";

// ---------------------------------------------------------------------------
// editorKit/useElementDrag — déplacement générique à la souris.
//
// Généralisé depuis FreeCanvas.onPointerDown/Move/Up (PageBuilder.jsx) :
// couvre à la fois le déplacement en position libre (écrit x/y) et le
// "reparenting" (détecte le conteneur survolé pendant le drag et permet
// d'y déposer l'élément), sans rien connaître du modèle de données de
// l'appelant — tout passe par des callbacks.
//
// Détection de cible de dépôt : chaque nœud rendu par PageBuilder est déjà
// entouré d'un wrapper `data-pb-node-id` (RenderNode). On part du vrai
// élément DOM sous le curseur (document.elementFromPoint), puis on remonte
// les ancêtres DOM via closest('[data-pb-node-id]') jusqu'à trouver un
// conteneur valide (isDroppable) — ou la racine du canvas (ROOT_PARENT_ID)
// si le curseur est au-dessus du canvas mais d'aucun conteneur particulier.
// ---------------------------------------------------------------------------

const NODE_ID_SELECTOR = "[data-pb-node-id]";

export function useElementDrag({
  canvasRef,          // ref du conteneur canvas (délimite la zone de dépôt valide)
  resolveNode,         // (id) => node | null
  isDroppable,          // (node, draggedId) => boolean
  onDragStart,          // (nodeId) => void
  onMove,               // (nodeId, x, y) => void — mode libre uniquement
  onGroupMove,          // (dx, dy) => void — déplacement groupé (multi-sélection)
  onHoverDropTarget,    // (containerId | null) => void — appelé quand la cible survolée change
  onDrop,               // (nodeId, containerId | null, moved) => void — containerId=null si aucune cible valide
  onDragEnd,            // (nodeId) => void — toujours appelé en dernier
} = {}) {
  const drag = useRef(null);

  const findDropTarget = useCallback((clientX, clientY, draggedId) => {
    const canvas = canvasRef?.current;
    if (!canvas) return null;
    const hitEl = document.elementFromPoint(clientX, clientY);
    if (!hitEl || !canvas.contains(hitEl)) return null;
    let el = hitEl.closest(NODE_ID_SELECTOR);
    while (el) {
      const id = el.getAttribute("data-pb-node-id");
      const node = id && resolveNode ? resolveNode(id) : null;
      if (node && id !== draggedId && (!isDroppable || isDroppable(node, draggedId))) {
        return id;
      }
      el = el.parentElement ? el.parentElement.closest(NODE_ID_SELECTOR) : null;
    }
    return ROOT_PARENT_ID;
  }, [canvasRef, resolveNode, isDroppable]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    d.moved = true;
    if (d.group) {
      onGroupMove?.(e.clientX - d.prevX, e.clientY - d.prevY);
      d.prevX = e.clientX;
      d.prevY = e.clientY;
    } else if (d.freeMode) {
      onMove?.(d.id, Math.max(0, e.clientX - d.offsetX), Math.max(0, e.clientY - d.offsetY));
    }
    if (onHoverDropTarget || onDrop) {
      const target = findDropTarget(e.clientX, e.clientY, d.id);
      if (target !== d.lastTarget) {
        d.lastTarget = target;
        onHoverDropTarget?.(target);
      }
    }
  }, [onMove, onGroupMove, onHoverDropTarget, onDrop, findDropTarget]);

  const onPointerUp = useCallback(() => {
    const d = drag.current;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    drag.current = null;
    if (!d) return;
    if (onDrop) onDrop(d.id, d.moved ? d.lastTarget : null, d.moved);
    onHoverDropTarget?.(null);
    onDragEnd?.(d.id);
  }, [onPointerMove, onDrop, onHoverDropTarget, onDragEnd]);

  // group: déplacement multi-sélection (dx/dy relatifs uniquement)
  // freeMode: écrit une position absolue x/y (mode "position libre")
  // pos: position courante {x,y} du nœud (mode libre uniquement)
  const startDrag = useCallback((e, node, { group = false, freeMode = false, pos = { x: 0, y: 0 } } = {}) => {
    drag.current = {
      id: node.id, group, freeMode, moved: false, lastTarget: null,
      offsetX: e.clientX - (pos.x || 0), offsetY: e.clientY - (pos.y || 0),
      prevX: e.clientX, prevY: e.clientY,
    };
    onDragStart?.(node.id);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }, [onDragStart, onPointerMove, onPointerUp]);

  return { startDrag, isDragging: () => !!drag.current };
}
